import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  IconButton,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Close, Create } from '@mui/icons-material';
import { useCreatePost, useUpdatePost } from '../../hooks/useCommunityMutations';
import type { CommunityPost } from '../../types/community';
import { COLORS } from '@elevair/shared';

const postSchema = z.object({
  title: z
    .string()
    .max(200, 'Title must be under 200 characters')
    .optional()
    .transform((val) => val?.trim() || undefined),
  content: z
    .string()
    .min(1, 'Content is required')
    .max(5000, 'Content must be under 5000 characters')
    .transform((val) => val.trim()),
});

type PostFormData = z.infer<typeof postSchema>;

interface CreatePostModalProps {
  open: boolean;
  onClose: () => void;
  editPost?: CommunityPost | null;
}

export default function CreatePostModal({
  open,
  onClose,
  editPost,
}: CreatePostModalProps) {
  const [error, setError] = useState<string | null>(null);
  const createPost = useCreatePost();
  const updatePost = useUpdatePost();

  const isEditing = !!editPost;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<PostFormData>({
    resolver: zodResolver(postSchema),
    defaultValues: {
      title: '',
      content: '',
    },
  });

  const content = watch('content');
  const characterCount = content?.length || 0;

  // Reset form when modal opens/closes or editPost changes
  useEffect(() => {
    if (open) {
      if (editPost) {
        reset({
          title: editPost.title || '',
          content: editPost.content,
        });
      } else {
        reset({
          title: '',
          content: '',
        });
      }
      setError(null);
    }
  }, [open, editPost, reset]);

  const onSubmit = async (data: PostFormData) => {
    setError(null);

    try {
      if (isEditing && editPost) {
        await updatePost.mutateAsync({
          id: editPost._id,
          data: {
            title: data.title,
            content: data.content,
          },
        });
      } else {
        await createPost.mutateAsync({
          title: data.title,
          content: data.content,
        });
      }
      handleClose();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || `Failed to ${isEditing ? 'update' : 'create'} post`);
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: `${COLORS.primary}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: COLORS.primary,
              }}
            >
              <Create />
            </Box>
            <Typography variant="h6">
              {isEditing ? 'Edit Post' : 'Create Post'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Title Field (Optional) */}
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Title (Optional)"
                fullWidth
                placeholder="Add a title to your post"
                error={!!errors.title}
                helperText={errors.title?.message}
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 200 }}
              />
            )}
          />

          {/* Content Field */}
          <Controller
            name="content"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="What's on your mind?"
                fullWidth
                multiline
                rows={6}
                placeholder="Share your thoughts, ask questions, or start a discussion..."
                error={!!errors.content}
                helperText={errors.content?.message}
                inputProps={{ maxLength: 5000 }}
              />
            )}
          />

          <Box display="flex" justifyContent="flex-end" mt={1}>
            <Typography
              variant="caption"
              color={characterCount > 4500 ? 'error.main' : 'text.secondary'}
            >
              {characterCount}/5000
            </Typography>
          </Box>

          <Box
            sx={{
              bgcolor: 'grey.50',
              borderRadius: 2,
              p: 2,
              mt: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Remember to follow our{' '}
              <Typography
                component="span"
                variant="caption"
                sx={{ color: COLORS.primary, cursor: 'pointer' }}
              >
                Community Guidelines
              </Typography>
              . Keep content professional and respectful.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: COLORS.accent,
              '&:hover': {
                backgroundColor: COLORS.accent,
                opacity: 0.9,
              },
            }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />
                {isEditing ? 'Saving...' : 'Posting...'}
              </>
            ) : isEditing ? (
              'Save Changes'
            ) : (
              'Post'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
